/**
 * OrgAvatar.tsx
 *
 * Avatar de organización con color dinámico de la paleta del sistema.
 * Forma cuadrada con radius-sm — más "institucional" que proyecto o usuario.
 * Típicamente aparece en el selector de workspace del sidebar.
 *
 * Uso:
 *   <OrgAvatar name="Innovaforce" color="oklch(0.69 0.20 300)" />
 *   <OrgAvatar name="Innovaforce" color="oklch(0.69 0.20 300)" src="/logo.svg" size="md" />
 */

import { cva, type VariantProps } from 'class-variance-authority';
import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { cn } from '@/core/lib/utils';
import { getTextColorFromToken, getColorValue } from '@/core/lib/colors';
import { getInitials } from '@/core/lib/get-initials';

/* ── Variantes ──────────────────────────────────────────────── */

const avatarVariants = cva('', {
  variants: {
    size: {
      xs: 'size-5 rounded-sm',
      sm: 'size-6 rounded-sm',
      md: 'size-8 rounded-sm',
      lg: 'size-10 rounded-sm',
      xl: 'size-12 rounded-sm',
      '2xl': 'size-[60px] md:size-[100px] rounded-xl',
    },
  },
  defaultVariants: { size: 'md' },
});

const fallbackVariants = cva('flex items-center justify-center font-medium select-none', {
  variants: {
    size: {
      xs: 'text-[9px] rounded-sm',
      sm: 'text-[10px] rounded-sm',
      md: 'text-xs rounded-sm',
      lg: 'text-sm rounded-sm',
      xl: 'text-base rounded-sm',
      '2xl': 'text-lg md:text-2xl rounded-xl',
    },
  },
  defaultVariants: { size: 'md' },
});

/* ── Props ──────────────────────────────────────────────────── */

export interface OrgAvatarProps extends VariantProps<typeof avatarVariants> {
  /** Nombre de la organización — usado para generar iniciales */
  name: string;
  /** Token del color de paleta (ej: "red-ember") — se resuelve internamente a oklch */
  color: string;
  /** URL de logo de la organización */
  src?: string;
  alt?: string;
  className?: string;
}

/* ── Componente ─────────────────────────────────────────────── */

export function OrgAvatar({ name, color, src, alt, size, className }: OrgAvatarProps) {
  const initials = getInitials(name);
  const colorValue = getColorValue(color);
  const textColor = getTextColorFromToken(color);

  return (
    <Avatar className={cn(avatarVariants({ size }), className)}>
      <AvatarImage src={src} alt={alt ?? name} />
      <AvatarFallback
        className={cn(fallbackVariants({ size }))}
        style={{ background: colorValue, color: textColor }}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}
